import React from 'react';
import { Star, Quote } from 'lucide-react';
import { Button } from './ui/button';

const SuccessStoriesSection = () => {
  const successStories = [
    {
      name: "Samir Mirza Baig",
      role: "DevOps Engineer at Leading Logistics Company",
      avatar: "https://ui-avatars.com/api/?name=Samir+Mirza+Baig&background=3b82f6&color=fff&size=150",
      story: "I was working night shifts at 7-Eleven, watching customers come and go while my IT degree collected dust at home. Every night, I wondered if I'd made the right choices. When I found CareerLift, everything changed. The instructors understood my situation - working full-time while trying to learn. They created a flexible schedule that worked around my shifts. Today, I'm a DevOps Engineer at a leading logistics company, and I finally feel like I'm living the life I studied for. CareerLift didn't just give me skills; they gave me back my confidence and my future.",
      rating: 5,
      program: "DevOps Engineer",
      previousJob: "7-Eleven Store Clerk"
    },
    {
      name: "Aleem Ahmad",
      role: "AI Engineer Trainee (Currently in Program)",
      avatar: "https://ui-avatars.com/api/?name=Aleem+Ahmad&background=10b981&color=fff&size=150",
      story: "For three years, I drove taxi 12 hours a day, seven days a week. My computer science degree felt like a distant memory. I started losing hope that I'd ever use my education. Then I discovered CareerLift's AI program. What amazed me was how they understood the financial pressure I was under - they even helped me plan my transition gradually so I could reduce my driving hours slowly. I'm now halfway through the AI Engineer program, and I already have two companies interested in hiring me upon completion. The practical projects we're doing are exactly what the industry needs. I can finally see light at the end of the tunnel.",
      rating: 5,
      program: "AI Engineer (In Progress)",
      previousJob: "Taxi Driver"
    },
    {
      name: "Shoaib Hassan",
      role: "IT Support Analyst (Career Transition in Progress)",
      avatar: "https://ui-avatars.com/api/?name=Shoaib+Hassan&background=8b5cf6&color=fff&size=150",
      story: "Driving for Uber seemed like easy money at first, but after two years, I realized I was going nowhere. My family kept asking why I wasn't using my IT diploma, and honestly, I had started to believe it was worthless. CareerLift changed that completely. The IT Support Analyst program was perfect for someone like me who needed to start somewhere solid. The hands-on labs made me remember why I loved technology in the first place. I'm currently in my final month of training, and I already have an interview lined up with a major tech company. The best part? The salary they're offering is more than I made in three months of driving. CareerLift helped me reclaim my dignity and my dreams.",
      rating: 5,
      program: "IT Support Analyst (Final Month)",
      previousJob: "Uber Driver"
    }
  ];

  return (
    <section id="success" className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-blue-50 to-green-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 lg:mb-16">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 lg:mb-6">
            Real Stories of Transformation
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            From survival jobs to dream careers - these are the journeys that inspire us every day
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 lg:gap-8 mb-10 lg:mb-16">
          {successStories.map((story, index) => (
            <div 
              key={index} 
              className="bg-white rounded-2xl p-4 sm:p-5 lg:p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
            >
              <div className="flex items-start mb-4">
                <Quote className="h-6 w-6 sm:h-8 sm:w-8 text-blue-500 mr-2 sm:mr-3 flex-shrink-0" />
                <div className="flex text-yellow-400">
                  {[...Array(story.rating)].map((_, i) => (
                    <Star key={i} className="h-3 w-3 sm:h-4 sm:w-4 fill-current" />
                  ))}
                </div>
              </div>
              
              <div className="mb-4">
                <span className="inline-block bg-red-100 text-red-800 text-xs font-medium px-2 sm:px-3 py-1 rounded-full">
                  Previously: {story.previousJob}
                </span>
              </div>
              
              <p className="text-gray-600 leading-relaxed mb-4 lg:mb-6 italic text-xs sm:text-sm">
                "{story.story}"
              </p>
              
              <div className="flex items-center gap-3 sm:gap-4">
                <img 
                  src={story.avatar} 
                  alt={story.name}
                  className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover flex-shrink-0"
                />
                <div className="min-w-0">
                  <div className="font-semibold text-gray-900 text-sm sm:text-base truncate">{story.name}</div>
                  <div className="text-xs sm:text-sm text-gray-600 truncate">{story.role}</div>
                  <div className="text-xs text-blue-600 font-medium">{story.program}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-r from-blue-600 to-green-600 rounded-2xl p-6 lg:p-8 text-center text-white">
          <h3 className="text-xl sm:text-2xl font-bold mb-3 lg:mb-4">Your Success Story Starts Here</h3>
          <p className="text-base sm:text-lg mb-4 lg:mb-6 text-blue-100">
            Join the growing community of professionals who transformed their lives with CareerLift
          </p>
          <Button 
            size="lg" 
            className="bg-white text-blue-600 hover:bg-gray-100 font-semibold px-6 lg:px-8 py-3 lg:py-4 touch-manipulation w-full sm:w-auto"
            onClick={() => window.location.href = '/contact'}
          >
            Schedule Your Career Progression Discussion
          </Button>
        </div>
      </div>
    </section>
  );
};

export default SuccessStoriesSection;